import { Box } from '@mui/material';
import ButtonCustom from 'components/ButtonCustom';
import GenericDialog from 'components/GenericDialog';
import UseAuthContext from 'context/AuthContext';
import { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getFeatures } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { hasSeenWhatsNew, markWhatsNewSeen } from 'utils/whatsNewSeen';

const POINT_KEYS = [
  'WHATS_NEW_POINT_PARALLEL',
  'WHATS_NEW_POINT_VERSIONS',
  'WHATS_NEW_POINT_STRATEGIES',
  'WHATS_NEW_POINT_ANALYSIS',
] as const;

const DASHBOARD_ROLES = new Set<string>([
  USER_ROLES.VIEWER,
  USER_ROLES.MEMBER,
  USER_ROLES.MANAGER,
  USER_ROLES.INSPECTOR,
  USER_ROLES.INSPECTOR_MANAGER,
]);

/**
 * Shown once per sign-in on the dashboard and inspections pages, for those
 * roles only, and only when the organization is listed in FEATURE_WHATS_NEW_IDS.
 * Closing it hides it until the next login. It stops coming back only when
 * that organization is removed from the flag.
 */
const WhatsNewModal = () => {
  const { isAuthenticated, user, role } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const userId = user?.id;
  const allowed = !!role && DASHBOARD_ROLES.has(role);
  const { data: features } = useQuery('features', getFeatures, {
    enabled: isAuthenticated && allowed,
  });
  const calendlyUrl = features?.calendlyMeetingUrl;
  const whatsNewAllowed = features?.whatsNewAllowed === true;

  useEffect(() => {
    if (!whatsNewAllowed) {
      setOpen(false);
      return;
    }
    if (!isAuthenticated || !userId || !allowed) return;
    if (hasSeenWhatsNew(userId)) return;
    markWhatsNewSeen(userId);
    setOpen(true);
  }, [isAuthenticated, userId, allowed, whatsNewAllowed]);

  return (
    <GenericDialog
      open={open}
      onClose={() => setOpen(false)}
      title={t('WHATS_NEW_TITLE')}
      maxWidth={false}
      fullWidth={false}
      paperSx={{ width: 'min(480px, calc(100vw - 32px))' }}
      contentSx={{ width: '100%', maxWidth: '100%', textAlign: 'center' }}
      contentAlign="center"
      actionsAlign="center"
      actions={
        <ButtonCustom
          color="primary"
          variant="contained"
          onClick={() => {
            setOpen(false);
            if (calendlyUrl) {
              window.open(calendlyUrl, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {calendlyUrl ? t('WHATS_NEW_CTA') : t('WHATS_NEW_CLOSE')}
        </ButtonCustom>
      }
    >
      <Box
        component="span"
        sx={{
          display: 'block',
          width: 'fit-content',
          maxWidth: '100%',
          mx: 'auto',
          textAlign: 'start',
        }}
      >
        {POINT_KEYS.map((key) => (
          <Box component="span" key={key} sx={{ display: 'block', '& + &': { mt: '10px' } }}>
            • {t(key)}
          </Box>
        ))}
      </Box>
    </GenericDialog>
  );
};

export default WhatsNewModal;
